/**
 * Utilitários de exportação do módulo Rotinas de Liderança (LST).
 * CSV (Excel), JSON (Duplicar Matriz / backup) e PDF (impressão da view).
 */

import {
  LST_CARGO_LABELS,
  LST_CATEGORY_LABELS,
  LST_FREQUENCY_LABELS,
  LST_STATUS_LABELS,
  type LstLideradoAderencia,
  type LstRotinaDoDia,
  type LstTemplate,
} from '../types';
import {
  LST_TEMPLATE_SCOPE_LABELS,
  resolveTemplateScope,
} from '../utils/lstTemplateScope';

function triggerDownload(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Escapa um campo para CSV (aspas + separador + quebra de linha). */
function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[";\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(rows: Array<Array<unknown>>): string {
  // BOM para acentuação correta no Excel + separador ';' (padrão pt-BR).
  return '\uFEFF' + rows.map((r) => r.map(csvCell).join(';')).join('\r\n');
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

/** Exporta o checklist do dia (Meu Padrão) para CSV/Excel. */
export function exportMeuPadraoCsv(rotinas: LstRotinaDoDia[], dateISO: string): void {
  const header = ['Horário', 'Rotina', 'Categoria', 'Frequência', 'Crítica', 'Status', 'Justificativa do desvio'];
  const body = rotinas.map((r) => [
    r.template.scheduled_time ?? '',
    r.template.title,
    r.template.category ? LST_CATEGORY_LABELS[r.template.category] : '',
    LST_FREQUENCY_LABELS[r.template.frequency],
    r.template.is_critical ? 'Sim' : 'Não',
    LST_STATUS_LABELS[r.status],
    r.execucao?.justificativa_desvio ?? '',
  ]);
  triggerDownload(toCsv([header, ...body]), `meu-padrao_${dateISO}.csv`, 'text/csv;charset=utf-8');
}

/** Exporta a matriz da Cadeia de Ajuda (aderência semanal) para CSV/Excel. */
export function exportCadeiaCsv(rows: LstLideradoAderencia[], weekDates: string[]): void {
  const ddmm = (iso: string) => `${iso.split('-')[2]}/${iso.split('-')[1]}`;
  const header = ['Liderado', 'Cargo', ...weekDates.map(ddmm), 'Desvios', 'Perdas'];
  const body = rows.map((row) => {
    const cells = row.dias.map((d) =>
      d.nivel === 'verde' ? 'OK' : d.nivel === 'laranja' ? 'Desvio' : d.nivel === 'vermelho' ? 'Perda' : '—',
    );
    const desvios = row.ocorrencias.filter((o) => o.status === 'realizado_com_desvio').length;
    const perdas = row.ocorrencias.filter((o) => o.status === 'nao_realizado').length;
    return [row.member.name, LST_CARGO_LABELS[row.cargo], ...cells, desvios, perdas];
  });
  triggerDownload(toCsv([header, ...body]), `cadeia-ajuda_${stamp()}.csv`, 'text/csv;charset=utf-8');
}

/** Exporta os padrões cadastrados (Configuração) para CSV/Excel. */
export function exportTemplatesCsv(
  templates: LstTemplate[],
  memberNameById?: Map<string, string>,
): void {
  const header = [
    'Cargo',
    'Escopo',
    'Usuário (exceção)',
    'Horário',
    'Rotina',
    'Descrição',
    'Categoria',
    'Frequência',
    'Período',
    'Crítica',
  ];
  const body = templates.map((t) => {
    const scope = resolveTemplateScope(t);
    const assignee =
      scope === 'user' && t.assigned_user_id
        ? memberNameById?.get(t.assigned_user_id) ?? t.assigned_user_id
        : '';
    return [
      LST_CARGO_LABELS[t.target_role],
      LST_TEMPLATE_SCOPE_LABELS[scope],
      assignee,
      t.scheduled_time ?? '',
      t.title,
      t.description ?? '',
      t.category ? LST_CATEGORY_LABELS[t.category] : '',
      LST_FREQUENCY_LABELS[t.frequency],
      t.period,
      t.is_critical ? 'Sim' : 'Não',
    ];
  });
  triggerDownload(toCsv([header, ...body]), `padroes-lst_${stamp()}.csv`, 'text/csv;charset=utf-8');
}

/**
 * "Salvar Como (Duplicar Matriz)" — exporta os padrões ativos como JSON portátil,
 * pronto para reimportar/duplicar a matriz de rotinas em outra organização.
 */
export function exportTemplatesJson(templates: LstTemplate[]): void {
  const payload = {
    kind: 'lst-template-matrix',
    version: 1,
    exported_at: new Date().toISOString(),
    count: templates.length,
    templates: templates.map((t) => ({
      target_role: t.target_role,
      scope: resolveTemplateScope(t),
      assigned_user_id: t.assigned_user_id ?? null,
      title: t.title,
      description: t.description,
      frequency: t.frequency,
      period: t.period,
      scheduled_time: t.scheduled_time,
      category: t.category,
      is_critical: t.is_critical,
      sort_order: t.sort_order,
    })),
  };
  triggerDownload(JSON.stringify(payload, null, 2), `matriz-lst_${stamp()}.json`, 'application/json');
}

/** Exporta a tela atual para PDF via diálogo de impressão do navegador. */
export function exportViewToPdf(): void {
  window.print();
}
